import React, { useRef, useEffect } from 'react';
import './KIWerkstatt-v2.css';
import KIWerkstattRegistrationForm from '../components/KIWerkstattRegistrationForm';
import RegionalPartnersSection from '../components/RegionalPartnersSection';

const KIWerkstattVerwaltung: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const [preSelectedRegion, setPreSelectedRegion] = React.useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('region') ?? '';
  });

  // If a region query param is present on first load, scroll to the registration form.
  useEffect(() => {
    const region = new URLSearchParams(window.location.search).get('region');
    if (!region) return;
    const timeoutId = window.setTimeout(() => {
      document.getElementById('registration-section')?.scrollIntoView({ behavior: 'smooth' });
    }, 150);
    return () => window.clearTimeout(timeoutId);
  }, []);

  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16;
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
    };

    applyMobileHeroOffset();
    window.addEventListener('resize', applyMobileHeroOffset, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', applyMobileHeroOffset as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  // Image Gallery Functionality for Maker Space section
  useEffect(() => {
    const setupGallery = (sectionSelector: string) => {
      const section = document.querySelector(sectionSelector);
      if (!section) return null;

      let currentSlide = 0;
      const slides = section.querySelectorAll('.gallery-slide');
      const benefitItems = section.querySelectorAll('.benefit-item');

      if (slides.length === 0) return null;

      const showSlide = (index: number) => {
        slides.forEach(slide => slide.classList.remove('active'));
        benefitItems.forEach(item => item.classList.remove('active'));

        slides[index]?.classList.add('active');
        benefitItems[index]?.classList.add('active');
        currentSlide = index;
      };

      const nextSlide = () => {
        currentSlide = (currentSlide + 1) % slides.length;
        showSlide(currentSlide);
      };

      let autoplayInterval = setInterval(nextSlide, 4000);

      const benefitClickHandlers: (() => void)[] = [];
      const benefitHoverHandlers: (() => void)[] = [];
      benefitItems.forEach((item, index) => {
        const handler = () => {
          showSlide(index);
          clearInterval(autoplayInterval);
          autoplayInterval = setInterval(nextSlide, 4000);
        };
        benefitClickHandlers.push(handler);
        benefitHoverHandlers.push(handler);
        item.addEventListener('click', handler);
        item.addEventListener('mouseenter', handler);
      });

      benefitItems[0]?.classList.add('active');

      return () => {
        clearInterval(autoplayInterval);
        benefitItems.forEach((item, index) => {
          item.removeEventListener('click', benefitClickHandlers[index]);
          item.removeEventListener('mouseenter', benefitHoverHandlers[index]);
        });
      };
    };

    const cleanup = setupGallery('[data-section="ki-werkstatt-verwaltung-system"]');

    return () => {
      cleanup?.();
    };
  }, []);

  return (
    <div className="page ki-werkstatt-page ki-werkstatt-verwaltung-page">
      {/* Hero Section */}
      <section
        ref={(el) => { heroRef.current = el; }}
        className="page-header ki-werkstatt-hero"
        data-section="ki-werkstatt-verwaltung-hero"
      >
        <div className="container">
          <div className="ki-werkstatt-hero-content">
            <span className="hero-pretitle">Ein Workshop-Tag in der</span>
            <h1 className="ki-werkstatt-hero-title">KI-Werkstatt</h1>
            <div className="hero-separator"></div>
            <p className="ki-werkstatt-hero-subtitle">
              ERLEBEN SIE DIE ZUKUNFT DER BÜROAUTOMATISIERUNG
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: Sichere Nutzung von RAG-Chatbots in der Verwaltung */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-system">
        <div className="container">
          <h2 className="section-title">Sichere Nutzung von <span className="highlight-text">RAG-Chatbots</span> in der Verwaltung</h2>
          <p className="section-subtitle">
            Ohne Betriebsgeheimnisse preis zu geben
          </p>
          <div className="ki-werkstatt-overlay-layout">
            <div className="overlay-gallery">
              <div className="gallery-container">
                <div className="gallery-slides">
                  <div className="gallery-slide active">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_hardware.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_hardware.jpeg" 
                        alt="KI-Werkstatt Hardware"
                        width="1200"
                        height="900"
                        loading="eager"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_software.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_software.jpeg" 
                        alt="KI-Werkstatt Software"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                  <div className="gallery-slide">
                    <picture>
                      <source srcSet="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.webp" type="image/webp" />
                      <img 
                        src="/assets/images/ki-werkstatt/kiwerkstatt_knowhow.jpeg" 
                        alt="KI-Werkstatt Know-How"
                        width="1200"
                        height="900"
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </picture>
                  </div>
                </div>
              </div>
            </div>
            <div className="overlay-benefits">
              <div className="benefits-list">
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Generative KI lokal betreiben</h4>
                    <p className="benefit-description">Lernen Sie, wie große Sprachmodelle und RAG-Systeme auf regulären Computern betrieben und zur Unterstützung von betrieblichen Aufgaben genutzt werden können.</p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Deine Betriebsdaten bleiben bei Dir</h4>
                    <p className="benefit-description">Teste KI-Anwendungsideen aus Deiner Verwaltung aus und experimentiere sorgenfrei mit sensiblen Betriebsdaten. Alles bleibt auf lokalen Computern und wird zum Ende des Workshops gelöscht.</p>
                  </div>
                </div>
                <div className="benefit-item benefit-overlay">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Erst testen, dann kaufen</h4>
                    <p className="benefit-description">Teste Deine KI-Anwendungsideen in der KI-Werkstatt mit aktuellster KI-Technik aus, bevor Dein Betrieb eigene Anschaffungen in Hardware und Software tätigt.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: KI-Souveränität */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-overview">
        <div className="container">
          <h2 className="section-title">Lass uns Deine <span className="highlight-text">KI-Souveränität</span> stärken</h2>
          <p className="section-subtitle">
            Mehr Verlässlichkeit, Kontrolle, Transparenz und Unabhängigkeit
          </p>
          <div className="souveraenity-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '2rem', marginTop: '2rem' }}>
            {/* Row 1 */}
            <div className="souveraenity-image">
              <picture>
                <source srcSet="/assets/images/ki-werkstatt/modelzoo2.webp" type="image/webp" />
                <img
                  src="/assets/images/ki-werkstatt/modelzoo2.png"
                  alt="Auswahl lokaler Sprachmodelle — Model Zoo Übersicht"
                  width="2624"
                  height="1938"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.25rem' }}>Welche lokale Sprachmodelle funktionieren für Deine Anwendungsidee?</h3>
              <p>
                In der KI-Werkstatt kannst Du eine große Vielfalt an Sprachmodellen direkt an Deiner Anwendungsidee ausprobieren. 
                Dabei kannst Du auch die Performance, Antwortqualität und Ressourcenbedarf auf verschiedener Hardware austesten. 
                Du lernst, welche konkrete Rechenleistung Du für die Umsetzung deines KI-Anwendungsfalls benötigst. 
                Am Ende des Workshops weißt Du genau, welche Sprachmodelle und welche Hardware für Deine spezifischen 
                Anforderungen optimal ist.
              </p>
            </div>

            {/* Row 2 */}
            <div className="souveraenity-image">
              <picture>
                <source srcSet="/assets/images/ki-werkstatt/confidential.webp" type="image/webp" />
                <img
                  src="/assets/images/ki-werkstatt/confidential.png"
                  alt="Vertrauliche Daten in lokalen RAG-Methoden und Algorithmen"
                  width="3028"
                  height="2560"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.25rem' }}>Wähle Deine optimalen RAG-Methoden und -Algorithmen</h3>
              <p>
                RAG-Systeme bestehen aus vielen technischen Komponenten, die je nach Anwendungsfall unterschiedlich konfiguriert 
                werden müssen. Im Workshop experimentierst Du mit verschiedenen Chunking-Strategien, Embedding-Modellen und 
                Retrieval-Algorithmen, um die optimale Konfiguration für Deine Dokumente zu finden. Du lernst die Stärken und 
                Schwächen unterschiedlicher Ansätze kennen und verstehst, wie diese die Antwortqualität beeinflussen. Mit diesem 
                Wissen kannst Du RAG-Systeme gezielt für Deine betrieblichen Anforderungen optimieren.
              </p>
            </div>

            {/* Row 3 */}
            <div className="souveraenity-image">
              <picture>
                <source srcSet="/assets/images/ki-werkstatt/docling.webp" type="image/webp" />
                <img
                  src="/assets/images/ki-werkstatt/docling.png"
                  alt="Docling — transparente Dokumentenverarbeitung in lokalen RAG-Systemen"
                  width="1934"
                  height="1526"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.25rem' }}>Verstehe, wie KI Deine Dokumente liest und korrigiere Fehler</h3>
              <p>
                Proprietäre KI-Systeme in der Cloud verarbeiten Deine Dokumente häufig in einer Black Box: Textextraktion, Chunking, 
                Embedding.. alles für Dich nicht überprüf- und korrigierbar. Bei lokal betriebenen RAG-Systemen sind alle RAG-Prozesse 
                transparent und von Dir gestaltbar. Du lernst typische Fehlerquellen bei der Verarbeitung von Informationen aus 
                betrieblichen Dokumenten kennen und wie Du diese systematisch behebst. Du lernst Software kennen, welche Dir die 
                Konfiguration von zuverlässigen RAG-Systemen ermöglicht.
              </p>
            </div>

            {/* Row 4 */}
            <div className="souveraenity-image">
              <picture>
                <source srcSet="/assets/images/ki-werkstatt/tokenlimit.webp" type="image/webp" />
                <img
                  src="/assets/images/ki-werkstatt/tokenlimit.png"
                  alt="Keine Token-Limits, keine Warteschlangen, keine Störungsausfälle"
                  width="2936"
                  height="2362"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
            <div>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.25rem' }}>Keine Nutzungskontingente, keine Warteschlangen, keine Störungsausfälle</h3>
              <p>
                Lokale KI-Systeme machen Dich unabhängig von Cloud-Anbietern und deren Limitierungen. Es gibt keine API-Rate-Limits, 
                keine überlasteten Server und keine Ausfälle durch Wartungsarbeiten externer Anbieter. Deine KI-Anwendungen laufen 
                stabil und zuverlässig auf Deiner eigenen Hardware. Du behältst die volle Kontrolle über Verfügbarkeit, Performance 
                und Kosten – ohne monatliche Abrechnungen pro API-Aufruf.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Hardware */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-hardware">
        <div className="container">
          <h2 className="section-title">Mit dieser <span className="highlight-text">Hardware</span> kannst Du experimentieren</h2>
          <p className="section-subtitle">
            bürotaugliche Hardware für den lokalen genAI-Betrieb
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', marginTop: '2rem' }}>
            {/* Row 1: Text/Descriptions */}
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.5rem' }}>Apple Silicon</h3>
              <p>Apple M4/M5, 64/128 GB VRAM</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.5rem' }}>Nvidia Jetson</h3>
              <p>AGX Orin (64GB), AGX Thor (128GB)</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '0.5rem' }}>Nvidia DGX</h3>
              <p>DGX Spark (128/256 GB)</p>
            </div>
          </div>
          
          {/* Row 2: Hardware Images with continuous background */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '2rem', 
            marginTop: '2rem',
            background: '#008F99',
            padding: '1rem 0',
            borderRadius: '12px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/assets/images/hardware/LA_macStudio.svg" 
                alt="Apple Silicon Mac Studio"
                style={{ height: '200px', width: 'auto', maxWidth: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/assets/images/hardware/LA_Thor.svg" 
                alt="Nvidia Jetson Thor"
                style={{ height: '200px', width: 'auto', maxWidth: '100%' }}
              />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img 
                src="/assets/images/hardware/dgx4.svg" 
                alt="Nvidia DGX Spark"
                style={{ height: '200px', width: 'auto', maxWidth: '100%' }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Software */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-software">
        <div className="container">
          <h2 className="section-title">Diese <span className="highlight-text">Software</span> ist für Dich vorkonfiguriert</h2>
          <p className="section-subtitle">
            Professionelle Tools für den lokalen RAG-Betrieb
          </p>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '2rem', marginTop: '2rem' }}>
            {/* Column 1: Lokaler Server */}
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '1rem' }}>Lokaler Server</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href="https://ollama.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>Ollama</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
                <a href="https://llama-cpp.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>Llama.cpp</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
                <a href="https://lmstudio.ai/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>LMStudio</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Column 2: Semi RAG-Chatbot */}
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '1rem' }}>Semi RAG-Chatbot</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href="https://openwebui.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>Open WebUI</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
                <a href="https://anythingllm.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>AnythingLLM</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
                <a href="https://lmstudio.ai/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>LMStudio</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
            
            {/* Column 3: Full RAG-Chatbot */}
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ color: '#008F99', fontSize: '1.4rem', marginBottom: '1rem' }}>Full RAG-Chatbot</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <a href="https://promptsense.eu" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                  <div className="software-product-tile">
                    <span>PromptSense</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                      <polyline points="15 3 21 3 21 9"></polyline>
                      <line x1="10" y1="14" x2="21" y2="3"></line>
                    </svg>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Schedule */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-schedule">
        <div className="container">
          <h2 className="section-title">Der <span className="highlight-text">Ablauf</span> Deines Workshop-Tags</h2>
          
          <div className="workshop-schedule">
            <div className="schedule-item">
              <div className="schedule-time">8:00 - 9:00</div>
              <div className="schedule-content">
              <h3>Einführung und theoretische Grundlagen</h3>
              <p>
                Technische Grundlagen von LLMs, RAG-Methoden und Rechnerarchitekturen für den lokalen KI-Betrieb. 
                Live-Demonstrationen u.a. aus den Bereichen technische Maschinenwartung, Ersatzteilbestellung und Normkonformität.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">9:00 - 10:00</div>
              <div className="schedule-content">
              <h3>Ausarbeitung eigener KI-Anwendungsfälle</h3>
              <p>
                Gemeinsame Erarbeitung Deines betrieblichen Anwendungsfalls und Deiner Betriebsdaten.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">10:00 - 12:00</div>
              <div className="schedule-content">
              <h3>Eigene Projektarbeit mit fachlicher Betreuung</h3>
              <p>
                Teste unterschiedliche KI-Modelle an Deinem betrieblichen Anwendungsfall und experimentiere mit unterschiedlichen Methoden zum Einlesen Deiner Betriebsdaten.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">12:00 - 13:00</div>
              <div className="schedule-content">
              <h3>Gemeinsames Mittagessen & Netzwerken</h3>
              <p>
                Gemeinsames Mittagessen vor Ort. Vernetzung und Austausch zwischen Teilnehmern und Dozenten.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">13:00 - 14:00</div>
              <div className="schedule-content">
              <h3>Zwischenevaluation der eigenen Projekte</h3>
              <p>
                Gemeinsame Zwischenevaluation der eigenen Projektarbeit. Bewertung der eingesetzten KI-Modelle und Methoden sowie Identifikation von Optimierungspotenzial.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">14:00 - 16:00</div>
              <div className="schedule-content">
              <h3>Fortsetzung der eigenen Projektarbeit</h3>
              <p>
                Fortsetzung der Projektarbeit in Eigenregie oder in Kleingruppen.
              </p>
              </div>
            </div>
            
            <div className="schedule-item">
              <div className="schedule-time">16:00 - 17:00</div>
              <div className="schedule-content">
              <h3>Entwicklung einer Roadmap für Dein Projekt</h3>
              <p>
                Übertragung von Gelerntem auf Anwendungsfelder der Teilnehmer. Gemeinsame Diskussion und fachliche Beurteilung eigener KI-Anwendungsideen. Ausarbeitung grober Roadmaps und technische Architekturen für die eigenen KI-Anwendungsideen.
              </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Regional Partners Map */}
      <RegionalPartnersSection onRegionSelect={setPreSelectedRegion} variant="verwaltung" />

      {/* Section 7: Workshop Details */}
      <section className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-details">
        <div className="container">
          <h2 className="section-title">Workshop <span className="highlight-text">Details</span></h2>
          
          <div className="workshop-grid">
            <div className="workshop-column">
              <h3>Zielgruppe</h3>
              <p>Fach- und Führungskräfte, IT-Verantwortliche und KI-Interessierte</p>
            </div>
            
            <div className="workshop-column">
              <h3>Voraussetzungen</h3>
              <p>Technisches Interesse. IT-Grundkenntnisse sind hilfreich, aber nicht zwingend erforderlich.</p>
            </div>
            
            <div className="workshop-column">
              <h3>Dauer</h3>
              <p>1 Tageskurs<br />08:00 - 17:00 Uhr</p>
            </div>
            
            <div className="workshop-column">
              <h3>Hardware</h3>
              <p>128GB Systeme (Apple M2/M3 Ultra, Nvidia) - High-End Workstations werden gestellt</p>
            </div>
            
            <div className="workshop-column">
              <h3>Praxis-Projekt</h3>
              <p>Aufbau eines Assistenzsystems für die Wartung von Anlagen und Maschinen mit lokalen LLMs</p>
            </div>
            
            <div className="workshop-column">
              <h3>Teilnahmegebühr</h3>
              <p><span className="highlight-text">990 €</span> netto pro Person</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 8: Registration */}
      <section id="registration-section" className="ki-werkstatt-section" data-section="ki-werkstatt-verwaltung-registration">
        <div className="container">
          <h2 className="section-title"><span className="highlight-text">Unverbindliche</span> Reservierung</h2>
          <p className="section-subtitle">
            DATUM WIRD MIT INTERESSIERTEN ABGESTIMMT
          </p>
          <KIWerkstattRegistrationForm variant="verwaltung" initialRegion={preSelectedRegion} />
        </div>
      </section>

      {/* Section 9: Cross-link to other variant */}
      <section className="ki-werkstatt-section variant-alternative" data-section="ki-werkstatt-localrag-alternative" style={{ background: '#008F99' }}>
        <div className="container">
          <h3 style={{ color: 'white' }}>Interessiert an KI-Automation in der Produktion?</h3>
          <p style={{ color: 'white' }}>Entdecke Computer Vision und Sensor-basierte Automation in der KI-Werkstatt</p>
          <a href="/ki-werkstatt/produktion" className="btn btn-secondary-light">
            Zur KI-Werkstatt "Produktion" →
          </a>
        </div>
      </section>
    </div>
  );
};

export default KIWerkstattVerwaltung;
